import { ApiError } from '@/shared/api'

/**
 * How long one open may take, session fetch and widget load together, and
 * how long a background call may take on its own.
 *
 * Without a bound, a session fetch that stalls holds `opening` up for as long
 * as the browser waits, and every later open queues behind it.
 */
export const CALL_BUDGET_MS = 15_000

/** A call or an open that ran past its budget. */
export class SupportChatTimeout extends Error {
  constructor() {
    super('the support chat did not answer in time')
    this.name = 'SupportChatTimeout'
  }
}

/**
 * One deadline, shared by every call made under it. `AbortSignal.timeout` is
 * newer than the browsers the build targets, hence the controller, as in
 * `useLogout`.
 */
export interface Budget {
  signal: AbortSignal
  /** Epoch milliseconds, for the widget load's own wait. */
  deadline: number
  /** Stops the timer once the work under it is done. */
  done(): void
}

export function startBudget(ms: number = CALL_BUDGET_MS): Budget {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  return {
    signal: controller.signal,
    deadline: Date.now() + ms,
    done: () => clearTimeout(timer),
  }
}

/**
 * Runs a call under a budget, reporting a lapse as `SupportChatTimeout`. The
 * call is raced against the budget too, so one that cannot take the signal —
 * a TanStack `fetchQuery` — still ends on time.
 */
export async function within<T>(
  budget: Budget,
  call: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  if (budget.signal.aborted) throw new SupportChatTimeout()
  let onAbort = () => undefined as void
  const lapsed = new Promise<never>((_, reject) => {
    onAbort = () => reject(new SupportChatTimeout())
    budget.signal.addEventListener('abort', onAbort, { once: true })
  })
  try {
    return await Promise.race([call(budget.signal), lapsed])
  } catch (error) {
    if (budget.signal.aborted) throw new SupportChatTimeout()
    throw error
  } finally {
    budget.signal.removeEventListener('abort', onAbort)
  }
}

/** Waits `ms`. The caller checks afterwards whether it is still wanted. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * A `429`'s wait, from `retry_after_seconds`, and never under a second, so a
 * zero cannot turn the retry into a tight loop. Waited out in the
 * background: a rate limit never holds an open.
 */
export function retryAfterMs(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.status !== 429) return null
  return Math.max(1, error.retryAfterSeconds ?? 1) * 1000
}
